import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import {
  duration,
  easing,
  layout,
  palette,
  space,
  spacing,
  typography,
} from '../../../../style-utils/theme';
import { m } from '../../../../motion';
import { TabTitle } from '../portal-kit.styled';

/**
 * THE VISITORS TAB'S OWN PIECES — the pass row and the pass page.
 *
 * Everything else (Section, Card, ListRow, RowTitle, RowMeta, RowPill) is the
 * portal kit; these are only what a pass needs on top of it: a whole row that
 * opens the pass (one generous target on a phone, not a thin name link), the
 * wrap rule every row with a pill obeys, and the pass page's fact lines.
 */

/* ONE ROW = ONE LINK. The motion wrapper carries the list cascade and the
   hairline between rows; the link inside carries the padding, so the whole row
   is the target and its focus ring hugs the row. */
export const PassRow = m.div`
  & + & {
    border-top: 1px solid ${palette.line};
  }
`;

export const PassLink = styled(Link)`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: ${spacing.xs} ${spacing.sm};
  min-height: ${layout.touchTarget};
  padding: ${spacing.sm} ${spacing.md};
  color: inherit;
  text-decoration: none;
  transition: background-color ${duration.fast} ${easing.standard};

  &:hover {
    background: ${palette.irisTintSoft};
  }

  /* The card clips its children (overflow hidden), so an outside ring would be
     cut off: the same 2px lagoon ring as shadow.focus, drawn INSIDE the row. */
  &:focus-visible {
    outline: 2px solid ${palette.irisDark};
    outline-offset: -2px;
  }
`;

/* The text column never gets squeezed under a pill: below this floor the pill
   wraps to its own line instead (the parcel-desk row rule). */
export const PassMain = styled.div`
  flex: 1 1 ${space(20)};
  min-width: 0;
`;

export const PassName = styled.div`
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  font-weight: ${typography.weight.semibold};
  line-height: ${typography.leading.snug};
  color: ${palette.ink};
  overflow-wrap: anywhere;
`;

export const PassLine = styled.div`
  margin-top: ${spacing.half};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
  overflow-wrap: anywhere;
`;

export const PassPills = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.xs};
  flex: 0 1 auto;
`;

/* A pill that sits inside a line of text (Recent visits' "On your pass"). */
export const InlinePills = styled.span`
  display: inline-flex;
  flex-wrap: wrap;
  gap: ${spacing.xs};
  margin-top: ${spacing.half};
`;

export const EndedToggle = styled.div`
  margin-top: ${spacing.sm};
`;

/* "Add a visitor", with the empty-state sentence or the limit reason above /
   under it, at the head of the Expected list. */
export const AddBar = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${spacing.xs};
  min-width: 0;
  margin: 0 0 ${spacing.sm};
`;

export const AddLead = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
  overflow-wrap: anywhere;
`;

/* ── The pass page ───────────────────────────────────────────────────────── */

export const DetailTop = styled.div`
  margin-bottom: ${spacing.sm};
`;

export const DetailPills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.xs};
  margin: 0 0 ${spacing.sm};
`;

export const DetailFacts = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  margin: 0 0 ${spacing.md};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.inkSoft};
  overflow-wrap: anywhere;
`;

export const DetailNote = styled.p`
  margin: 0;
  color: ${palette.slate};
`;

/* The page's h1. Focused from code once a pass is ended (the End button it
   came from has gone), never tabbed to. */
export const DetailTitle = styled(TabTitle)`
  &:focus {
    outline: none;
  }
`;

/* Pause / Resume, Edit and End — a row that wraps, never scrolls sideways. */
export const DetailActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.xs};
  min-width: 0;
  margin: 0 0 ${spacing.md};
`;

export const ActionsNote = styled.p`
  flex: 1 0 100%;
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
  overflow-wrap: anywhere;
`;

/* ONE UPCOMING VISIT, with its Skip / Undo skip. The portal ListRow with the
   row rule every row that holds an action obeys: the date keeps a text floor
   and the pill and button wrap to their own line below it. */
export const VisitRow = m.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.xs} ${spacing.sm};
  min-width: 0;
  padding: ${spacing.sm} ${spacing.md};

  & + & {
    border-top: 1px solid ${palette.line};
  }
`;

export const VisitMain = styled.div`
  flex: 1 1 ${space(20)};
  min-width: 0;
  overflow-wrap: anywhere;
`;

export const VisitSide = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.xs};
  flex: 0 1 auto;
  min-width: 0;
`;
